import React, { useState, useEffect } from "react";
import axios from "../api/axios"; // axios instance

const BannerForm = () => {
  const [type, setType] = useState("hero"); // hero | slider
  const [title, setTitle] = useState("");
  const [images, setImages] = useState([null, null, null]); // store up to 3 images
  const [previews, setPreviews] = useState([null, null, null]); // preview URLs
  const [loading, setLoading] = useState(false);

  const [banners, setBanners] = useState([]); // fetched banners

  // Fetch banners on mount
  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const res = await axios.get("/banners");
      setBanners(res.data || []);
    } catch (error) {
      console.error("Error fetching banners:", error);
    }
  };

  // Handle type change
  const handleTypeChange = (e) => {
    setType(e.target.value);
    setImages([null, null, null]);
    setPreviews([null, null, null]);
  };

  // Handle individual image selection
  const handleImageChange = (e, index) => {
    const file = e.target.files[0] || null;
    const newImages = [...images];
    const newPreviews = [...previews];

    newImages[index] = file;
    newPreviews[index] = file ? URL.createObjectURL(file) : null;

    setImages(newImages);
    setPreviews(newPreviews);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title) return alert("Please enter a title.");

    if (type === "hero" && !images[0]) {
      return alert("Hero banner requires exactly 1 image.");
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("type", type);

      images.forEach((img) => {
        if (img) formData.append("images[]", img);
      });

      await axios.post("/admin/banners-create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("✅ Banner saved successfully!");
      setTitle("");
      setImages([null, null, null]);
      setPreviews([null, null, null]);
      fetchBanners(); // refresh list
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "❌ Failed to save banner");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete banner
const handleDelete = async (id) => {
  if (!window.confirm("Are you sure you want to delete this banner?")) return;
  try {
    await axios.delete(`/admin/banners-delete/${id}`);
    alert("🗑️ Banner deleted successfully!");
    
    // Optimistically remove from state
    setBanners((prev) => prev.filter((banner) => banner.id !== id));
    
    // Optionally re-fetch to stay in sync
    fetchBanners();
  } catch (error) {
    console.error("Delete error:", error.response || error);
    alert("❌ Failed to delete banner");
  }
};


  return (
    <div className="container mt-4">
      {/* Form */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Add Banner / Slider</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Banner type */}
            <div className="form-group">
              <label>Banner Type</label>
              <select
                className="form-control"
                value={type}
                onChange={handleTypeChange}
              >
                <option value="hero">Hero Banner</option>
                <option value="slider">Slider</option>
              </select>
            </div>

            {/* Title */}
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter banner title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Image upload */}
            {type === "hero" && (
              <div className="form-group">
                <label>Banner Image</label>
                <input
                  type="file"
                  className="form-control-file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, 0)}
                />
                {previews[0] && (
                  <img
                    src={previews[0]}
                    alt="Preview"
                    className="mt-2"
                    style={{ maxHeight: "120px", border: "1px solid #ddd" }}
                  />
                )}
              </div>
            )}

            {type === "slider" && (
              <div className="form-group">
                <label>Slider Images (up to 3)</label>
                {[0, 1, 2].map((index) => (
                  <div key={index} className="mb-3">
                    <input
                      type="file"
                      className="form-control-file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, index)}
                    />
                    {previews[index] && (
                      <img
                        src={previews[index]}
                        alt={`Preview ${index + 1}`}
                        className="mt-2"
                        style={{
                          maxHeight: "120px",
                          border: "1px solid #ddd",
                          marginRight: "10px",
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-success mt-3"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Banner"}
            </button>
          </form>
        </div>
      </div>

      {/* Banner List */}
      <div className="card shadow-sm">
        <div className="card-header bg-secondary text-white">
          <h4 className="mb-0">Existing Banners</h4>
        </div>
        <div className="card-body">
          {banners.length === 0 ? (
            <p>No banners found.</p>
          ) : (
            <div className="row">
              {banners.map((banner) => (
                <div className="col-md-4 mb-3" key={banner.id}>
                  <div className="card h-100">
                    <div className="card-body text-center">
                      <h5>{banner.title}</h5>
                      {banner.images.map((img, i) => (
                        <img
                          key={i}
                          src={`https://cartbackend.wsdstech.com/storage/${img}`}
                          alt="Banner"
                          className="img-fluid mb-2"
                          style={{ maxHeight: "150px" }}
                        />
                      ))}
                      <p className="text-muted small">
                        Type: {banner.type.toUpperCase()}
                      </p>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(banner.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BannerForm;
